// lib
import { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { DataContext as SidebarDataContext } from '@aytm/app/stats/page/sidebar/components/context'

import useNonInitialEffect from '@aytm/app/stats/page/components/hooks/useNonInitialEffect'

import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

import { getQuestionId, getQuestionIcon, removeLogicFromText } from '@aytm/app/stats/page/components/lib/utils'
import { toggleCheckbox } from '@aytm/app/stats/page/sidebar/components/lib/utils'
import { encodeText, pluralize } from '@aytm/app/stats/lib/util/helpers'
import { convertTraits } from '@aytm/app/stats/lib/query/traits'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// components
import { Button } from 'primereact/button'
import { RadioButton } from 'primereact/radiobutton'
import { Checkbox } from 'primereact/checkbox'
import { Dropdown } from 'primereact/dropdown'

import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'

// styles
import style from '@aytm/app/stats/page/sidebar/export/styles/ExportPanel.module.scss'

const levelsOptions = (PageGlobals.CONFIDENCE_LEVELS || []).map(level => {
  return { key: level[0], text: level[1] }
})

const NEVER_DERIVED = Symbol('never derived')

const percentDecimalsOptions = Array.from({ length: 4 }, (_, i) => {
  return { key: i, text: String(i) }
})

const getBannerKey = question => {
  const id = getQuestionId(question)

  const type = question.variant || question.type
  const key = `${id}${'psc' == type ? 'psc' : ''}`

  return 'dlx' == question.type
    ? Object.hasOwn(question, 'batches')
      ? `${key}:${question.batches.map(b => `sq${b.position}`).join('-')}`
      : `${key}:${question.answerVariants.map(av => `a${av.position}`).join('-')}`
    : key
}

const Crosstab = ({ root, survey, model, report, features, processing, onExport, onParamsChange }) => {
  const { sidebarState, sidebarDispatch } = useContext(SidebarDataContext)

  const disabled = features.correlation_scan.disabled

  const { responses, totalRespondents } = model

  const traits = useMemo(
    () =>
      Array.isArray(survey.options.crosstabTraits)
        ? convertTraits(survey).reduce((acc, cur) => {
            if (!survey.options.crosstabTraits.includes(cur.id)) return acc
            if ('country' == cur.id && 'WRD' != survey.country.id) return acc
            if (cur.missing || !Object.hasOwn(survey.refsList || {}, cur.id.toUpperCase())) return acc
            acc.push(cur)
            return acc
          }, [])
        : [],
    [survey.country, survey.options, survey.refsList]
  )

  const variables = useMemo(
    () =>
      Object.keys(survey.customVariables || {}).reduce((acc, cur) => {
        if (
          !(true == survey.customVariables[cur].long || 0 < survey.customVariables[cur].values.length) ||
          !/conjoint_cluster\d+/.test(cur)
        )
          return acc

        acc.push(Object.assign({ key: cur }, survey.customVariables[cur]))
        return acc
      }, []),
    [survey.customVariables]
  )

  const questions = useMemo(() => {
    const reg = new RegExp('^xq(\\d+)(?:[_](\\d+))?$', 'i')
    const xquestions = Object.hasOwn(survey.scripts || {}, 'questions')
      ? Object.values(report.current.virtual)
          .filter(q => reg.test(q.id))
          .reduce((acc, cur) => {
            const q = Object.values(survey.scripts.questions).find(
              q => q.id.startsWith(`${cur.id}_`) && q.blocks.some(b => 'chart' == b.type)
            )
            if (q) {
              const chart = q.blocks.find(b => 'chart' == b.type)
              acc.push(
                Object.assign({}, chart, {
                  id: cur.id,
                  key: q.id,
                  text: cur.text,
                  type: ('horizontal-bars' == chart.subtype && 'rb') || chart.subtype,
                  variant: 'dlx',
                  title: q.text,
                  position: 0
                })
              )
            }
            return acc
          }, [])
      : []

    return Object.values(report.current.virtual)
      .map(q => {
        const _q = JSON.parse(JSON.stringify(q))
        _q.type = q.variant
        _q.variant = q.type
        return _q
      })
      .concat(survey.questions.filter(q => report.current.schema.includes(`q${q.position}`)))
      .reduce((acc, cur) => {
        // a virtual question arrives with its type and variant swapped, so an Xpert question names
        // `dlx` as its variant here
        if ('dlx' == cur.variant && 'gap-analysis' == cur.script) return acc

        const q = 'dlx' == cur.variant ? xquestions.find(xq => xq.id == cur.id) : cur
        if (!q || 'none' == q.analysis) return acc

        if (!PageGlobals.ALLOW_QUESTION_TYPES.includes(q.type)) return acc

        if (
          ['rb', 'cb', 'scx', 'sft'].includes(q.type) &&
          (!q.answerVariants?.length || 1000 <= (q.answerVariants.length * (q.answerVariants.length - 1)) / 2)
        )
          return acc
        if (
          ['mx', 'se', 'sr', 'mp'].includes(q.type) &&
          (!q.batches?.length ||
            1000 <=
              (q.batches.length * q.batches[0].answerVariants.length * (q.batches[0].answerVariants.length - 1)) / 2)
        )
          return acc
        if (
          ['qa', 'bpm'].includes(q.type) &&
          (!q.questionVariants?.length ||
            !q.batches?.length ||
            1000 <= (q.questionVariants.length * q.batches.length * (q.batches.length - 1)) / 2)
        )
          return acc
        if (
          ['ro', 'aro'].includes(q.type) &&
          (!q.batches?.length || 1000 <= (q.batches.length * q.batches.length * (q.batches.length - 1)) / 2)
        )
          return acc
        if (['psc'].includes(q.type) && (!q.batches?.length || 1000 <= 3 * q.batches.length)) return acc

        acc.push(q)
        return acc
      }, [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey.questions, survey.scripts, report.current.schema, report.current.virtual])

  const topsModes = useMemo(
    () =>
      questions.reduce((acc, cur) => {
        const id = (0 < cur.position && cur.position) || cur.id
        const key = (0 < cur.position && cur.position) || cur.key || cur.id

        if (report.current.params[`tcv${key}`] != undefined) {
          const tcv = report.current.params[`tcv${key}`].split('|')

          if ('boxScore' == tcv[0]) acc[id] = tcv[1] || 'top1'
          if ('meanScore' == tcv[0]) acc[id] = 'means'
        }
        return acc
      }, {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questions, report.current.params]
  )

  const [clExportVisibility, setClExportVisibility] = useState('hide' == model.clExportVisibility)

  const [banners, setBanners] = useState(sidebarState.export.crosstab.banners || [])

  const [top2Box, setTop2Box] = useState(sidebarState.export.crosstab.top2Box || false)
  const [top3Box, setTop3Box] = useState(sidebarState.export.crosstab.top3Box || false)
  const [top15Box, setTop15Box] = useState(sidebarState.export.crosstab.top15Box || false)

  const [showMean, setShowMean] = useState(sidebarState.export.crosstab.showMean || false)

  const [showCounts, setShowCounts] = useState(
    Object.hasOwn(sidebarState, 'export') && Object.hasOwn(sidebarState.export.crosstab, 'showCounts')
      ? sidebarState.export.crosstab.showCounts
      : true
  )
  const [showPercents, setShowPercents] = useState(
    Object.hasOwn(sidebarState, 'export') && Object.hasOwn(sidebarState.export.crosstab, 'showPercents')
      ? sidebarState.export.crosstab.showPercents
      : true
  )

  const [confidenceLevel, setConfidenceLevel] = useState(sidebarState.export.crosstab.confidenceLevel || 0.95)
  const [significanceLevel, setSignificanceLevel] = useState(sidebarState.export.crosstab.significanceLevel || 0.9)
  const [percentDecimals, setPercentDecimals] = useState(
    Object.hasOwn(sidebarState, 'export') && Object.hasOwn(sidebarState.export.crosstab, 'percentDecimals')
      ? sidebarState.export.crosstab.percentDecimals
      : 1
  )

  const [showTesting, setShowTesting] = useState(
    Object.hasOwn(sidebarState, 'export') && Object.hasOwn(sidebarState.export.crosstab, 'showTesting')
      ? sidebarState.export.crosstab.showTesting
      : true
  )
  const [sigTestMode, setSigTestMode] = useState('all')

  const timer = useRef(null)

  const optionsRef = useRef(null)
  const optionsScrollTopRef = useRef(null)

  const bannersRef = useRef(null)
  bannersRef.current = banners

  const confidenceLevelRef = useRef(null)
  confidenceLevelRef.current = confidenceLevel
  const significanceLevelRef = useRef(null)
  significanceLevelRef.current = significanceLevel

  // Survives the percentages-off state, which the panel represents as a null precision.
  const lastPercentDecimals = useRef(null)

  // Which report the summary metrics were last derived for; the sentinel means "not yet".
  const derivedForReport = useRef(NEVER_DERIVED)
  // What each of the four derived to last time, so only a metric whose hint moved is re-derived.
  const lastDerived = useRef(null)

  const extra = useRef({})
  extra.current = {
    confidenceLevel,
    significanceLevel,
    percentDecimals,
    top15Box,
    top2Box,
    top3Box,
    showMean,
    showCounts,
    showPercents,
    showTesting,
    showPValues: true,
    ...(showTesting && { sigTestMode })
  }

  const bannersOptions = useMemo(
    () =>
      traits
        .reduce(
          (acc, trait) =>
            banners.some(banner => banner.key == trait.id)
              ? acc
              : // Title is researcher-supplied and the banner label renders as innerHTML; encode it
                // so a hostile title stays inert text. Mirrors view-options Demographic (OQ-17).
                acc.concat([{ key: trait.id, text: encodeText(trait.title), value: trait, type: 'trait' }]),
          []
        )
        .concat(
          variables.reduce(
            (acc, variable) =>
              banners.some(banner => banner.key == variable.key)
                ? acc
                : acc.concat([{ key: variable.key, text: variable.key, value: variable, type: 'variable' }]),
            []
          )
        )
        .concat(
          questions.reduce((acc, question) => {
            const id = getQuestionId(question)
            const key = getBannerKey(question)

            const text = `${id.toUpperCase()}: ${removeLogicFromText(question.text)}`

            const icon = getQuestionIcon(question)

            return banners.some(banner => banner.key == key)
              ? acc
              : acc.concat([{ key, text, icon, value: question, type: 'question' }])
          }, [])
        ),
    [traits, variables, questions, banners]
  )

  const overLimit = useMemo(() => {
    const numberOfConnections = banners.reduce((amount, banner) => {
      if (['trait', 'variable'].includes(banner.type)) {
        return amount + (banner.value.values?.length || 0)
      }

      if ('question' == banner.type) {
        const question = banner.value

        const type = question.variant || question.type

        if (['rb', 'cb', 'scx', 'sft'].includes(type))
          return amount + (question.answerVariants.length * (question.answerVariants.length - 1)) / 2
        if (['mx', 'se', 'sr', 'mp'].includes(type))
          return (
            amount +
            (question.batches.length *
              question.batches[0].answerVariants.length *
              (question.batches[0].answerVariants.length - 1)) /
              2
          )
        if (['qa', 'bpm'].includes(type))
          return (
            amount + (question.questionVariants.length * question.batches.length * (question.batches.length - 1)) / 2
          )
        if (['ro', 'aro'].includes(type))
          return amount + (question.batches.length * question.batches.length * (question.batches.length - 1)) / 2
        if (['psc'].includes(type)) return amount + 3 * question.batches.length
      }

      return amount
    }, 0)

    return 1000 <= numberOfConnections
  }, [banners])

  const exportDisabled = useMemo(
    () =>
      disabled ||
      overLimit ||
      !questions.length ||
      responses < PageGlobals.ANALYSIS_UNLOCK_N ||
      totalRespondents < PageGlobals.ANALYSIS_UNLOCK_N,
    [responses, totalRespondents, questions, disabled, overLimit]
  )

  const crossByLogic = useMemo(() => {
    const option = bannersOptions.find(banner => 'question' == banner.type)

    return ((0 < banners.length && banners) || (option != undefined && [option]) || []).reduce((acc, banner) => {
      if (['trait', 'variable'].includes(banner.type)) {
        acc.push(banner.key)
      }

      if ('question' == banner.type) {
        const question = banner.value

        const key =
          0 < question.position
            ? `${(question.pre_type && 'PQ') || 'Q'}${question.visual_position}`
            : question.id.toUpperCase()
        const type = question.variant || question.type

        if (Object.hasOwn(question, 'questionVariants') && Object.hasOwn(question, 'batches')) {
          for (const qv of question.questionVariants) {
            for (const b of question.batches) {
              acc.push(`${key}E${qv.position}SQ${b.position}`)
            }
          }
        } else if (Object.hasOwn(question, 'batches')) {
          for (const b of question.batches) {
            acc.push(`${key}${['ro', 'aro'].includes(type) ? 'A' : 'SQ'}${b.position}`)
          }
        } else acc.push(`${key}`)
      }

      return acc
    }, [])
  }, [banners, bannersOptions])

  const itemTemplate = option =>
    option.disabled && option.icon == undefined ? (
      <span dangerouslySetInnerHTML={{ __html: option.label }}></span>
    ) : (
      <span className={option.item ? '' : 'p-item-label'}>
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.text }}></span>
      </span>
    )
  const valueTemplate = (option, _props) =>
    option ? (
      <>
        {option.icon && <i className={`p-item-icon icon-aytmfd-${option.icon}`} />}
        <span dangerouslySetInnerHTML={{ __html: option.label || option.text }}></span>
      </>
    ) : (
      <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }}></span>
    )

  const onDataFormatChange = (counts, percents) => {
    setShowCounts(counts)
    setShowPercents(percents)

    // The precision still has to be nulled on the way out: it is the value the export payload
    // carries, and a live precision beside percentages-off is a shape the wire has never seen. So
    // the choice is remembered beside it rather than in it, and restored when percentages return.
    if (null !== percentDecimals) lastPercentDecimals.current = percentDecimals
    setPercentDecimals(percents ? percentDecimals ?? lastPercentDecimals.current ?? 1 : null)
  }

  const onBannerHide = () => {
    const overlay = optionsRef.current.getOverlay()

    const wrapper = overlay.querySelector('.p-dropdown-items-wrapper')
    optionsScrollTopRef.current = wrapper.scrollTop
  }

  const onBannerShow = useCallback(() => {
    const overlay = optionsRef.current.getOverlay()

    const wrapper = overlay.querySelector('.p-dropdown-items-wrapper')
    wrapper.scrollTop = optionsScrollTopRef.current

    timer.current && clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      overlay.classList.add('p-rendered')
    })

    return () => timer.current && clearTimeout(timer.current)
  }, [])

  const onBannerSelect = useCallback(
    value => {
      const option = bannersOptions.find(_option => _option.key == value)
      setBanners(banners.concat([option]))
    },
    [banners, bannersOptions]
  )

  const onBannerRemove = useCallback(value => setBanners(banners.filter(_banner => _banner.key != value)), [banners])

  // The comparison mode is deliberately per-session, unlike its neighbours in this panel, but it
  // holds within one: turning significance testing off and on again leaves the chosen mode alone.
  const onShowTestingToggle = useCallback(() => setShowTesting(value => !value), [])

  const onExportClick = useCallback(() => {
    'function' == typeof onExport &&
      onExport({
        fileType: 'crosstab',
        props: { noCache: true, clExportVisibility },
        options: { crossByLogic, extra: extra.current }
      })
  }, [crossByLogic, clExportVisibility, onExport])

  useEffect(
    () => (optionsScrollTopRef.current = 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [traits, variables, questions]
  )

  useEffect(() => {
    if (disabled) {
      setBanners([])
    } else {
      setBanners(
        bannersRef.current
          .map(banner => {
            if ('trait' == banner.type) {
              const trait = traits.find(_trait => _trait.id == banner.key)
              if (trait != undefined) {
                banner.value = trait
                return banner
              }
            }
            if ('variable' == banner.type) {
              const variable = variables.find(_variable => _variable.key == banner.key)
              if (variable != undefined) {
                banner.value = variable
                return banner
              }
            }
            if ('question' == banner.type) {
              const question = questions.find(_question => getBannerKey(_question) == banner.key)
              if (question != undefined) {
                banner.value = question
                return banner
              }
            }

            return null
          })
          .filter(banner => null !== banner)
      )
    }
  }, [traits, variables, questions, disabled])

  useEffect(() => {
    if (disabled) {
      setTop2Box(false)
      setTop3Box(false)
      setTop15Box(false)
      setShowMean(false)
      derivedForReport.current = NEVER_DERIVED // re-enabling derives afresh, as it did before
      lastDerived.current = null
      return
    }
    const derived = questions.reduce(
      (acc, question) => {
        const topsMode = topsModes[(0 < question.position && question.position) || question.id]
        return [
          acc[0] || ['top2', 'bottom2'].includes(topsMode) || false,
          acc[1] || ['top3', 'bottom3'].includes(topsMode) || false,
          acc[2] || ['top2w', 'bottom2w'].includes(topsMode) || false,
          acc[3] || ['means', 'meansReversed'].includes(topsMode) || false
        ]
      },
      [false, false, false, false]
    )
    const setters = [setTop2Box, setTop3Box, setTop15Box, setShowMean]

    // A report switch snaps all four to the new hints. Otherwise only a metric whose derived value
    // moved -- a question's view changed -- follows it, so a tick the reader set by hand on another
    // metric, or a re-render that rebuilds identical inputs, leaves that tick alone.
    if (derivedForReport.current !== report.current?.id || null === lastDerived.current) {
      setters.forEach((set, i) => set(derived[i]))
    } else {
      setters.forEach((set, i) => derived[i] !== lastDerived.current[i] && set(derived[i]))
    }

    lastDerived.current = derived
    derivedForReport.current = report.current?.id
  }, [questions, topsModes, disabled, report])

  useEffect(() => setSignificanceLevel(Math.min(significanceLevelRef.current, confidenceLevel)), [confidenceLevel])
  useEffect(() => setConfidenceLevel(Math.max(confidenceLevelRef.current, significanceLevel)), [significanceLevel])

  useEffect(
    () =>
      sidebarDispatch({
        type: 'UPDATE_STATE',
        key: 'export',
        state: {
          crosstab: { ...sidebarState.export.crosstab, banners }
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [banners]
  )
  useEffect(
    () =>
      sidebarDispatch({
        type: 'UPDATE_STATE',
        key: 'export',
        state: {
          crosstab: { ...sidebarState.export.crosstab, showCounts, showPercents, percentDecimals }
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showCounts, showPercents, percentDecimals]
  )
  useEffect(
    () =>
      sidebarDispatch({
        type: 'UPDATE_STATE',
        key: 'export',
        state: {
          crosstab: { ...sidebarState.export.crosstab, confidenceLevel, significanceLevel }
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [confidenceLevel, significanceLevel]
  )
  useEffect(
    () =>
      sidebarDispatch({
        type: 'UPDATE_STATE',
        key: 'export',
        state: {
          crosstab: { ...sidebarState.export.crosstab, showTesting }
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showTesting]
  )
  useEffect(
    () =>
      sidebarDispatch({
        type: 'UPDATE_STATE',
        key: 'export',
        state: {
          crosstab: { ...sidebarState.export.crosstab, top2Box, top3Box, top15Box }
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [top2Box, top3Box, top15Box]
  )
  useEffect(
    () =>
      sidebarDispatch({
        type: 'UPDATE_STATE',
        key: 'export',
        state: {
          crosstab: { ...sidebarState.export.crosstab, showMean }
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showMean]
  )

  useNonInitialEffect(
    () =>
      onParamsChange(
        {
          cl_export_visibility: clExportVisibility ? 'hide' : 'show'
        },
        false
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clExportVisibility, onParamsChange]
  )

  // useEffect(() => {}, [sidebarState.export.crosstab])

  const renderBanner = banner => (
    <div data-testid={`stat-exports-crosstab-banner-${banner.key}`} className={style.button}>
      <span
        data-testid={`stat-exports-crosstab-banner-${banner.key}-delete`}
        className={style['button-delete']}
        aria-label="Remove banner"
        {...pressable(() => onBannerRemove(banner.key))}
      >
        <i className="icon-aytmfd-x-v2" />
      </span>
      {banner.icon ? <i className={`icon-aytmfd-${banner.icon}`} /> : null}
      <span className={style['button-text']}>
        {'question' == banner.type ? getQuestionId(banner.value).toUpperCase() : banner.text}
      </span>
    </div>
  )

  const renderBannersGroup = () => (
    <div className={style.section}>
      <div className={style['section-title']}>
        <b>Banner Groups</b>
        {root && (
          <SidebarPopup
            root={root}
            help="Choose subgroups or categories for your analysis. Selected subgroups will make up the headers in your banner tables export. <br/><br/><a href='https://helpcenter.aytm.com/hc/en-us/articles/17200144374029-Banner-Tables' target='_blank' rel='noopener noreferrer'>Learn more &raquo;</a>"
            width="300px"
          />
        )}
      </div>

      {banners.map(banner => (
        <div key={banner.key} className={style.row}>
          {renderBanner(banner)}
        </div>
      ))}
      <div className={style.row}>
        <div className="p-fluid p-col-12">
          <Dropdown
            ref={optionsRef}
            id="stat-exports-crosstab-add-banner-selector"
            data-testid="stat-exports-crosstab-add-banner-selector"
            className="p-dropdown-rounded p-dropdown-light-blue"
            placeholder="Add banner"
            value={null}
            options={bannersOptions}
            optionValue="key"
            optionLabel="text"
            itemTemplate={itemTemplate}
            valueTemplate={valueTemplate}
            filter
            filterBy="search,text"
            filterPlaceholder="Search items"
            resetFilterOnHide
            filterInputAutoFocus={false}
            confirmKeyboardSelect
            fixedDropdownDir="bottom"
            disabled={disabled || overLimit}
            onChange={disabled || overLimit ? null : event => onBannerSelect(event.value)}
            onShow={disabled || overLimit ? null : onBannerShow}
            onHide={disabled || overLimit ? null : onBannerHide}
          />
        </div>
      </div>
    </div>
  )

  const renderDataFormat = () => (
    <div className={style.section}>
      <div className={style['section-title']}>
        <b>Data Format</b>
        {root && (
          <SidebarPopup
            root={root}
            help='Choose to show counts, percentages, or both. If "percentages" or "both" are selected, customize the rounding to just the whole number, or up to 3 decimal places. This setting will apply to all question types in your banner export, including Concept Lab reports, TURF analyses, and more.'
            width="300px"
          />
        )}
      </div>

      <div className={`${style.row} ${style['row-indent']}`}>
        <div className={`${style.param} p-col-12`}>
          <RadioButton
            data-testid="stat-exports-crosstab-show-counts"
            className="p-radiobutton-sm"
            aria-labelledby="stat-exports-crosstab-show-counts-label"
            value="showCounts"
            checked={showCounts && !showPercents}
            disabled={disabled}
            onChange={disabled ? null : () => onDataFormatChange(true, false)}
          />
          <span
            data-testid="stat-exports-crosstab-show-counts-label"
            id="stat-exports-crosstab-show-counts-label"
            className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
            onClick={disabled ? null : () => onDataFormatChange(true, false)}
          >
            Show counts
          </span>
        </div>
      </div>
      <div className={`${style.row} ${style['row-indent']}`}>
        <div className={`${style.param} p-col-12`}>
          <RadioButton
            data-testid="stat-exports-crosstab-show-percentages"
            className="p-radiobutton-sm"
            aria-labelledby="stat-exports-crosstab-show-percentages-label"
            value="showPercents"
            checked={!showCounts && showPercents}
            disabled={disabled}
            onChange={disabled ? null : () => onDataFormatChange(false, true)}
          />
          <span
            data-testid="stat-exports-crosstab-show-percentages-label"
            id="stat-exports-crosstab-show-percentages-label"
            className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
            onClick={disabled ? null : () => onDataFormatChange(false, true)}
          >
            Show percentages
          </span>
        </div>
      </div>
      <div className={`${style.row} ${style['row-indent']}`}>
        <div className={`${style.param} p-col-12`}>
          <RadioButton
            data-testid="stat-exports-crosstab-show-both"
            className="p-radiobutton-sm"
            aria-labelledby="stat-exports-crosstab-show-both-label"
            value="showBoth"
            checked={showCounts && showPercents}
            disabled={disabled}
            onChange={disabled ? null : () => onDataFormatChange(true, true)}
          />
          <span
            data-testid="stat-exports-crosstab-show-both-label"
            id="stat-exports-crosstab-show-both-label"
            className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
            onClick={disabled ? null : () => onDataFormatChange(true, true)}
          >
            Show both
          </span>
        </div>
      </div>
      {showPercents ? (
        <div className={`${style.row} ${style['row-indent']}`}>
          <div className={`${style.param} p-col`}>
            <Checkbox
              data-testid="stat-exports-crosstab-dataRounding"
              className="p-checkbox-sm"
              aria-labelledby="stat-exports-crosstab-dataRounding-label"
              value="dataRounding"
              checked={null !== percentDecimals}
              disabled={disabled}
              onChange={disabled ? null : () => setPercentDecimals(null === percentDecimals ? 1 : null)}
              onKeyDown={
                disabled
                  ? null
                  : event => toggleCheckbox(event, () => setPercentDecimals(null === percentDecimals ? 1 : null))
              }
            />
            <span
              data-testid="stat-exports-crosstab-dataRounding-label"
              id="stat-exports-crosstab-dataRounding-label"
              className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
              onClick={disabled ? null : () => setPercentDecimals(null === percentDecimals ? 1 : null)}
            >
              Percentage decimal place
            </span>
          </div>
          {null === percentDecimals ? null : (
            <div className="p-fluid p-col" style={{ flex: '0 0 auto', width: '27%', margin: '-8px 0 -10px' }}>
              <Dropdown
                data-testid="stat-exports-crosstab-dataRounding-selector"
                className="p-dropdown-sm p-dropdown-rounded"
                aria-labelledby="stat-exports-crosstab-dataRounding-label"
                ariaLabel="Percentage decimal place"
                value={percentDecimals}
                options={percentDecimalsOptions}
                optionValue="key"
                optionLabel="text"
                fixedDropdownDir="bottom"
                filter={false}
                disabled={null !== processing || disabled}
                onChange={event => setPercentDecimals(event.value)}
              />
            </div>
          )}
        </div>
      ) : null}
    </div>
  )

  const renderSignificanceTesting = () => (
    <>
      <div className={style.section}>
        <div className={style['section-title']}>
          <b>Significance Testing</b>
          {root && (
            <SidebarPopup
              root={root}
              help="Confidence level determines how certain a researcher can be regarding their sample accurately revealing the attitudes of the total population being tested. Primary CL, usually set to 95% for market research purposes, is used for a variety of significance tests on this page and in statistical reports. In Banner Table reports, Primary CL designates statistically significant difference at 95% and is marked with upper case letters. Secondary CL defaults to 90% and is signified with lower case letters. Total only tests each banner column against the overall Total. Within banners only tests columns pairwise within each banner group. Total and within banners applies both. <br/><br/> <a href='https://helpcenter.aytm.com/hc/en-us/articles/360059216031-Significance-Testing-with-aytm' target='_blank' rel='noopener noreferrer'>Learn more about Significance Testing with aytm</a>"
              width="300px"
            />
          )}
        </div>

        <div className={classNames(style.row, style['row-double'])}>
          <div className="p-fluid p-col">
            <span id="stat-exports-crosstab-confidence-level-label" className={style['row-note']}>
              Primary
            </span>
            <Dropdown
              id="stat-exports-crosstab-confidence-level"
              data-testid="stat-exports-crosstab-confidence-level"
              className="p-dropdown-rounded"
              aria-labelledby="stat-exports-crosstab-confidence-level-label"
              ariaLabel="Primary confidence level"
              value={confidenceLevel}
              options={levelsOptions}
              optionValue="key"
              optionLabel="text"
              fixedDropdownDir="bottom"
              filter={false}
              disabled={null !== processing || disabled}
              onChange={event => setConfidenceLevel(event.value)}
            />
          </div>
          <div className="p-fluid p-col">
            <span id="stat-exports-crosstab-significance-level-label" className={style['row-note']}>
              Secondary
            </span>
            <Dropdown
              id="stat-exports-crosstab-significance-level"
              data-testid="stat-exports-crosstab-significance-level"
              className="p-dropdown-rounded"
              aria-labelledby="stat-exports-crosstab-significance-level-label"
              ariaLabel="Secondary confidence level"
              value={significanceLevel}
              options={levelsOptions}
              optionValue="key"
              optionLabel="text"
              fixedDropdownDir="bottom"
              filter={false}
              disabled={null !== processing || disabled}
              onChange={event => setSignificanceLevel(event.value)}
            />
          </div>
        </div>
      </div>
      <div className={style.section}>
        <div className={`${style.row} ${style['row-indent']}`}>
          <div className={`${style.param} p-col-12`}>
            <Checkbox
              data-testid="stat-exports-crosstab-show-significance"
              className="p-checkbox-sm"
              aria-labelledby="stat-exports-crosstab-show-significance-label"
              value="testing"
              checked={showTesting}
              disabled={disabled}
              onChange={disabled ? null : onShowTestingToggle}
              onKeyDown={disabled ? null : event => toggleCheckbox(event, onShowTestingToggle)}
            />
            <span
              data-testid="stat-exports-crosstab-show-significance-label"
              id="stat-exports-crosstab-show-significance-label"
              className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
              onClick={disabled ? null : onShowTestingToggle}
            >
              Show significance testing
            </span>
          </div>
        </div>
        {showTesting ? (
          <>
            <div className={`${style.row} ${style['row-indent']}`}>
              <div className={`${style.param} p-col-12`}>
                <RadioButton
                  data-testid="stat-exports-crosstab-sigtest-totals"
                  className="p-radiobutton-sm"
                  aria-labelledby="stat-exports-crosstab-sigtest-totals-label"
                  value="totals"
                  checked={sigTestMode == 'totals'}
                  disabled={disabled}
                  onChange={disabled ? null : () => setSigTestMode('totals')}
                />
                <span
                  data-testid="stat-exports-crosstab-sigtest-totals-label"
                  id="stat-exports-crosstab-sigtest-totals-label"
                  className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                  onClick={disabled ? null : () => setSigTestMode('totals')}
                >
                  Compare against total only
                </span>
              </div>
            </div>
            <div className={`${style.row} ${style['row-indent']}`}>
              <div className={`${style.param} p-col-12`}>
                <RadioButton
                  data-testid="stat-exports-crosstab-sigtest-banners"
                  className="p-radiobutton-sm"
                  aria-labelledby="stat-exports-crosstab-sigtest-banners-label"
                  value="banners"
                  checked={sigTestMode == 'banners'}
                  disabled={disabled}
                  onChange={disabled ? null : () => setSigTestMode('banners')}
                />
                <span
                  data-testid="stat-exports-crosstab-sigtest-banners-label"
                  id="stat-exports-crosstab-sigtest-banners-label"
                  className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                  onClick={disabled ? null : () => setSigTestMode('banners')}
                >
                  Compare within banners only
                </span>
              </div>
            </div>
            <div className={`${style.row} ${style['row-indent']}`}>
              <div className={`${style.param} p-col-12`}>
                <RadioButton
                  data-testid="stat-exports-crosstab-sigtest-all"
                  className="p-radiobutton-sm"
                  aria-labelledby="stat-exports-crosstab-sigtest-all-label"
                  value="all"
                  checked={sigTestMode == 'all'}
                  disabled={disabled}
                  onChange={disabled ? null : () => setSigTestMode('all')}
                />
                <span
                  data-testid="stat-exports-crosstab-sigtest-all-label"
                  id="stat-exports-crosstab-sigtest-all-label"
                  className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                  onClick={disabled ? null : () => setSigTestMode('all')}
                >
                  Compare against total and within banners
                </span>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </>
  )

  const renderSummaryMetrics = () => (
    <div className={style.section}>
      <div className={style['section-title']}>
        <b>Summary Metrics</b>
        {root && (
          <SidebarPopup
            root={root}
            help="Select box scores or metrics to include in your export. Summary metrics will automatically be applied to all applicable question types: radio buttons, progressive matrix, sliders (matrix), and star/smiley rating. <br/><br/> <a href='https://helpcenter.aytm.com/hc/en-us/articles/17200144374029-Banner-Tables' target='_blank' rel='noopener noreferrer'>Learn more about Banner Tables</a>"
            width="300px"
          />
        )}
      </div>

      <div className={`${style.row} ${style['row-indent']}`}>
        <div className={`${style.param} p-col-12`}>
          <Checkbox
            data-testid="stat-exports-crosstab-top2box"
            className="p-checkbox-sm"
            aria-labelledby="stat-exports-crosstab-top2box-label"
            value="top2box"
            checked={top2Box}
            disabled={disabled}
            onChange={disabled ? null : () => setTop2Box(!top2Box)}
            onKeyDown={disabled ? null : event => toggleCheckbox(event, () => setTop2Box(!top2Box))}
          />
          <span
            data-testid="stat-exports-crosstab-top2box-label"
            id="stat-exports-crosstab-top2box-label"
            className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
            onClick={disabled ? null : () => setTop2Box(!top2Box)}
          >
            Top and Bottom 2 Box
          </span>
        </div>
      </div>
      <div className={`${style.row} ${style['row-indent']}`}>
        <div className={`${style.param} p-col-12`}>
          <Checkbox
            data-testid="stat-exports-crosstab-top3box"
            className="p-checkbox-sm"
            aria-labelledby="stat-exports-crosstab-top3box-label"
            value="top3box"
            checked={top3Box}
            disabled={disabled}
            onChange={disabled ? null : () => setTop3Box(!top3Box)}
            onKeyDown={disabled ? null : event => toggleCheckbox(event, () => setTop3Box(!top3Box))}
          />
          <span
            data-testid="stat-exports-crosstab-top3box-label"
            id="stat-exports-crosstab-top3box-label"
            className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
            onClick={disabled ? null : () => setTop3Box(!top3Box)}
          >
            Top and Bottom 3 Box
          </span>
        </div>
      </div>
      <div className={`${style.row} ${style['row-indent']}`}>
        <div className={`${style.param} p-col-12`}>
          <Checkbox
            data-testid="stat-exports-crosstab-top15box"
            className="p-checkbox-sm"
            aria-labelledby="stat-exports-crosstab-top15box-label"
            value="top15box"
            checked={top15Box}
            disabled={disabled}
            onChange={disabled ? null : () => setTop15Box(!top15Box)}
            onKeyDown={disabled ? null : event => toggleCheckbox(event, () => setTop15Box(!top15Box))}
          />
          <span
            data-testid="stat-exports-crosstab-top15box-label"
            id="stat-exports-crosstab-top15box-label"
            className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
            onClick={disabled ? null : () => setTop15Box(!top15Box)}
          >
            Top and Bottom 1.5 Box
          </span>
        </div>
      </div>
      <div className={`${style.row} ${style['row-indent']}`}>
        <div className={`${style.param} p-col-12`}>
          <Checkbox
            data-testid="stat-exports-crosstab-mean"
            className="p-checkbox-sm"
            aria-labelledby="stat-exports-crosstab-mean-label"
            value="mean"
            checked={showMean}
            disabled={disabled}
            onChange={disabled ? null : () => setShowMean(!showMean)}
            onKeyDown={disabled ? null : event => toggleCheckbox(event, () => setShowMean(!showMean))}
          />
          <span
            data-testid="stat-exports-crosstab-mean-label"
            id="stat-exports-crosstab-mean-label"
            className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
            onClick={disabled ? null : () => setShowMean(!showMean)}
          >
            Mean
          </span>
        </div>
      </div>
    </div>
  )

  return (
    <>
      {renderBannersGroup()}
      {renderDataFormat()}
      {renderSignificanceTesting()}
      {renderSummaryMetrics()}

      {survey?.cl_exists ? (
        <div className={style.section}>
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-exports-crosstab-cl-export-visibility"
                className="p-checkbox-sm"
                aria-labelledby="stat-exports-crosstab-cl-export-visibility-label"
                value="cl-export-visibility"
                checked={clExportVisibility}
                disabled={disabled}
                onChange={disabled ? null : () => setClExportVisibility(!clExportVisibility)}
                onKeyDown={
                  disabled ? null : event => toggleCheckbox(event, () => setClExportVisibility(!clExportVisibility))
                }
              />
              <span
                data-testid="stat-exports-crosstab-cl-export-visibility-label"
                id="stat-exports-crosstab-cl-export-visibility-label"
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setClExportVisibility(!clExportVisibility)}
              >
                Hide aytm Logic
              </span>
            </div>
          </div>
        </div>
      ) : null}

      <div className={style.section}>
        <div className={style.row}>
          <Button
            data-testid="stat-exports-crosstab-export"
            className="p-button-rounded p-button-blue"
            label={'crosstab' == processing ? 'Downloading' : 'Run & Download XLSX'}
            loading={'crosstab' == processing}
            disabled={null !== processing || exportDisabled}
            onClick={null !== processing || exportDisabled ? null : onExportClick}
          />
        </div>

        {overLimit ? (
          <div className={`${style.row} ${style['row-error']}`}>
            Please reduce the number of banners selected in order to run and download banner tables.
          </div>
        ) : responses < PageGlobals.ANALYSIS_UNLOCK_N ? (
          <div className={`${style.row} ${style['row-error']}`}>
            You need at least {PageGlobals.ANALYSIS_UNLOCK_N}{' '}
            {pluralize(PageGlobals.ANALYSIS_UNLOCK_N, 'respondent', 'respondents')} to create banner tables. Current
            filter settings show {responses} {pluralize(responses, 'respondent', 'respondents')}. Remove some filters or
            order more respondents to proceed.
          </div>
        ) : totalRespondents < PageGlobals.ANALYSIS_UNLOCK_N ? (
          <div className={`${style.row} ${style['row-error']}`}>
            You need at least {PageGlobals.ANALYSIS_UNLOCK_N}{' '}
            {pluralize(PageGlobals.ANALYSIS_UNLOCK_N, 'respondent', 'respondents')} to create banner tables. This survey
            currently contains only {totalRespondents} {pluralize(totalRespondents, 'complete', 'completes')}.
          </div>
        ) : !questions.length ? (
          <div className={`${style.row} ${style['row-error']}`}>
            You need at least 1 question in order to run and download banner tables.
          </div>
        ) : null}
      </div>
    </>
  )
}

Crosstab.propTypes = {
  root: PropTypes.any,
  survey: PropTypes.object,
  model: PropTypes.object,
  report: PropTypes.object,
  features: PropTypes.object,
  processing: PropTypes.string,
  onExport: PropTypes.func,
  onParamsChange: PropTypes.func
}

Crosstab.displayName = 'Crosstab'

export default Crosstab
